import { Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { blueGrey } from '@mui/material/colors';
import { format } from 'date-fns';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IExpandedTourDate } from '../../models/IExpandedTourDate';
import { formatAvailableSpotsLabel } from '../../utils/formatAvailableSpots';
import ReservationModal from '../reservation/ReservationModal';

interface WebViewProps {
    tourDates: IExpandedTourDate[];
    selectedTourId: number;
}

const WebView: React.FC<WebViewProps> = ({ tourDates, selectedTourId }) => {
    const { t } = useTranslation();

    const [selectedTour, setSelectedTour] = useState<IExpandedTourDate | null>(null);

    return (
        <>
            <TableContainer component={Paper} sx={{ boxShadow: "rgba(9, 30, 66, 0.25) 0px 1px 1px, rgba(9, 30, 66, 0.13) 0px 0px 1px 1px" }}>
                <Table>
                    <TableHead sx={(theme) => ({
                        backgroundColor: blueGrey[50],
                        ...theme.applyStyles("dark", {
                            backgroundColor: theme.palette.background.default
                        })
                    })}>
                        <TableRow>
                            <TableCell>
                                <Typography color={"text2.primary"} variant='subtitle2' fontWeight={600}>{t("tourDetails.common.terms.from")}</Typography>
                            </TableCell>
                            <TableCell>
                                <Typography color={"text2.primary"} variant='subtitle2' fontWeight={600}>{t("tourDetails.common.terms.to")}</Typography>
                            </TableCell>
                            <TableCell>
                                <Typography color={"text2.primary"} variant='subtitle2' fontWeight={600}>{t("tourDetails.common.terms.days")}</Typography>
                            </TableCell>
                            <TableCell>
                                <Typography color={"text2.primary"} variant='subtitle2' fontWeight={600}>{t("tourDetails.common.terms.availableSpots")}</Typography>
                            </TableCell>
                            <TableCell>
                                <Typography color={"text2.primary"} variant='subtitle2' fontWeight={600}>{t("tourDetails.common.terms.price")}</Typography>
                            </TableCell>
                            <TableCell></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {tourDates.map(date => {
                            const isSelectedTour = date.tour.id === selectedTourId;
                            const isFull = date.availableSpots === 0;

                            return (
                                <TableRow
                                    key={date.id}
                                    sx={(theme) => ({
                                        '&:hover': {
                                            backgroundColor: blueGrey[50],
                                        },
                                        ...theme.applyStyles('dark', {
                                            '&:hover': {
                                                backgroundColor: theme.palette.action.hover,
                                            },
                                        }),
                                    })}
                                >
                                    <TableCell>
                                        <Typography variant='subtitle2' color={isSelectedTour ? 'text.primary' : 'text.secondary'}>
                                            {format(new Date(date.startDate), 'dd.MM.yyyy')}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant='subtitle2' color={isSelectedTour ? 'text.primary' : 'text.secondary'}>
                                            {format(new Date(date.endDate), 'dd.MM.yyyy')}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            variant='subtitle2'
                                            fontWeight={isSelectedTour ? 700 : 500}
                                            color={isSelectedTour ? 'text.primary' : 'text.secondary'}
                                        >
                                            {t(date.tour.name)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography
                                            variant='subtitle2'
                                            fontWeight={600}
                                            color={isFull ? 'error.main' : (isSelectedTour ? 'text.primary' : 'text.secondary')}
                                        >
                                            {isFull
                                                ? t('tourDetails.common.terms.fullyBooked')
                                                : formatAvailableSpotsLabel(date.availableSpots, t)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant='subtitle2' color={isSelectedTour ? 'text.primary' : 'text.secondary'}>
                                            {date.tour.price} €
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Button
                                            color='secondary'
                                            variant="contained"
                                            size="small"
                                            disabled={isFull}
                                            onClick={() => !isFull && setSelectedTour(date)}
                                        >
                                            {t("tourDetails.common.terms.reserve")}
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <ReservationModal
                open={!!selectedTour}
                onClose={() => setSelectedTour(null)}
                tourDate={selectedTour}
            />
        </>
    )
}

export default WebView;